import React from "react";
import * as Icons from "lucide-react";
import { Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import PhotonLogo from "./PhotonLogo";
import { footer, company } from "../mock";
import useSettings from "../hooks/useSettings";
import useBranding from "../hooks/useBranding";
import { brandText } from "../utils/brand";

const LINK_MAP = {
  "Privacy Policy": "/policies/privacy-policy",
  "Terms & Conditions": "/policies/terms-and-conditions",
  "Refund Policy": "/policies/refund-policy",
  "Grievance Policy": "/policies/grievance-policy",
  "About Us": "/#about",
  "Industries": "/#industries",
  "Blog": "/blog",
  "Contact Us": "/#cta",
  "Careers": "/#cta",
  "BBPS Solutions": "/services/bbps-solutions",
  "POS Solutions": "/services/pos-solutions",
  "FasTag": "/services/fastag-solutions",
  "Photon Collect": "/services/photon-collect",
  "Photon Disburse": "/services/photon-disburse",
};

const Footer = () => {
  const navigate = useNavigate();
  const contact = useSettings();
  const b = useBranding();
  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const goto = (label) => {
    const to = LINK_MAP[label] || "/";
    navigate(to);
    window.scrollTo({ top: 0 });
  };

  return (
    <footer id="footer" className="relative text-white" style={{ background: "linear-gradient(160deg,#050C17,#0D1B2A)" }}>
      <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle at 90% 10%, #06B6D4 0, transparent 40%)" }} />
      <div className="pp-container relative py-16">
        <div className="mb-12 pb-8 border-b border-white/10" data-testid="footer-legal-name">
          <div className="text-2xl md:text-4xl font-extrabold tracking-tight uppercase leading-tight">
            {b.company_name} <span className="text-[#06B6D4]">–</span> <span className="text-white/50 normal-case font-semibold text-lg md:text-2xl">A Brand of</span>{" "}
            <span className="pp-grad-text">{company.legalName}</span>
          </div>
        </div>
        <div className="grid lg:grid-cols-[1.4fr_1fr_1fr_1fr] gap-10">
          <div>
            <div className="bg-white inline-block rounded-xl px-3 py-2">
              <PhotonLogo variant="dark" />
            </div>
            <p className="mt-5 text-white/65 text-sm leading-relaxed max-w-sm">{brandText(footer.about, b.company_name)}</p>
            <div className="mt-6 space-y-2.5 text-sm">
              <a href={`tel:${contact.phoneRaw}`} data-testid="footer-phone" className="flex items-center gap-3 text-white/75 hover:text-[#10B981] transition-colors">
                <Phone size={16} className="text-[#10B981]" /> {contact.phone}
              </a>
              <a href={`mailto:${contact.email}`} data-testid="footer-email" className="flex items-center gap-3 text-white/75 hover:text-[#10B981] transition-colors">
                <Mail size={16} className="text-[#10B981]" /> {contact.email}
              </a>
              {company.offices.map((o, i) => (
                <div key={o.label} className="flex items-start gap-3 text-white/75" data-testid={`footer-office-${i}`}>
                  <MapPin size={16} className="text-[#10B981] mt-0.5 shrink-0" />
                  <span><b className="text-white/90 font-semibold">{o.label}:</b> {o.address}</span>
                </div>
              ))}
            </div>
          </div>

          {footer.columns.map((col) => (
            <div key={col.title}>
              <h4 className="font-bold text-white mb-4">{col.title}</h4>
              <ul className="space-y-2.5">
                {col.links.map((l) => (
                  <li key={l}>
                    <button onClick={() => goto(l)} className="text-sm text-white/65 hover:text-[#10B981] transition-colors flex items-center gap-2 group text-left">
                      <ArrowRight size={13} className="opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all text-[#10B981]" />
                      {l}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-white/55">© {new Date().getFullYear()} {b.company_name} — a brand of {company.legalName} All rights reserved.{b.tagline ? ` ${b.tagline}.` : ""}</p>
          <div className="flex items-center gap-3">
            {footer.socials.map((s) => {
              const Icon = Icons[s] || Icons.Globe;
              return (
                <a key={s} href="#top" onClick={scrollTop} className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#06B6D4] flex items-center justify-center transition-colors">
                  <Icon size={16} />
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
